/**
 * The one decision-and-write that seeds content into a note's Y.XmlFragment.
 *
 * Both seed paths run it: web's Supabase→DO legacy migration
 * (`seedNotesFromLegacyIfEmpty`, which wraps this in the note's transaction)
 * and mobile's notes editor (legacy migration AND a transcription deck's
 * generated notes). They used to be two ~50-line copies whose error handling
 * had already drifted apart.
 *
 * Three properties are load-bearing and easy to lose in a re-implementation:
 *
 *  1. It refuses to write over anything that isn't "effectively empty", so it
 *     is safe to re-attempt on every mount and can never clobber an edit made
 *     on another device.
 *  2. It writes the fragment DIRECTLY rather than through the editor's command
 *     chain, so seeded content is not a user-undoable step — otherwise one
 *     undo after opening the tab wipes the note.
 *  3. `skipped` means "the content did not land" — a caller holding a
 *     retryable stash MUST keep it, or the note is lost for good.
 *
 * ── Why the converters are parameters ──────────────────────────────────
 * They are the reason this could not simply be imported by both sides. The
 * sync-client resolves `prosemirrorJSONToYXmlFragment` from `@tiptap/y-tiptap`,
 * which flashka-app's Metro unconditionally redirects to a THROWING stub (its
 * prosemirror-model setup crashes the RN bundle under Hermes — see
 * metro.config.js `SHIM_REDIRECTS`). Mobile's DOM editor therefore uses
 * `y-prosemirror` directly. Injecting them keeps this module free of either
 * import, so it changes no one's module graph and cannot be stubbed out from
 * under a caller.
 *
 * It also takes a bare fragment rather than a `ScopedNotes` facade, because
 * mobile's editor owns its own `Y.Doc` and never goes through sync-client.
 */
import type * as Y from 'yjs';

import { isNoteDocEffectivelyEmpty } from './note-emptiness';

export type NoteSeedResult =
  /** Content was written into the fragment. */
  | 'seeded'
  /** The note already held real content — left untouched. */
  | 'already-present'
  /** Nothing to seed, or the conversion failed. Callers holding a retryable
   *  stash MUST keep it on this result. */
  | 'skipped';

/** The Yjs↔ProseMirror pair, supplied by the caller (see the module docblock). */
export interface NoteFragmentConverters {
  /** Y.XmlFragment → ProseMirror JSON. */
  toJSON: (fragment: Y.XmlFragment) => unknown;
  /** ProseMirror JSON → Y.XmlFragment. APPENDS; it does not replace. */
  fromJSON: (schema: unknown, content: unknown, fragment: Y.XmlFragment) => unknown;
}

export interface SeedNoteFragmentParams {
  fragment: Y.XmlFragment;
  /** The LIVE editor's ProseMirror schema. Without it the converter silently
   *  drops node and mark types the schema doesn't declare. */
  schema: unknown;
  /** ProseMirror JSON to seed. */
  content: unknown;
  converters: NoteFragmentConverters;
}

/**
 * Seed `content` into `fragment` if — and only if — the fragment is
 * effectively empty.
 */
export function seedNoteFragmentIfEmpty({
  fragment,
  schema,
  content,
  converters,
}: SeedNoteFragmentParams): NoteSeedResult {
  if (!content) return 'skipped';

  let currentJSON: unknown;
  try {
    currentJSON = fragment.length > 0 ? converters.toJSON(fragment) : undefined;
  } catch {
    // An unreadable fragment counts as empty: better to seed than to strand the
    // user with a note that can never be populated.
    currentJSON = undefined;
  }

  if (!isNoteDocEffectivelyEmpty(currentJSON)) return 'already-present';

  try {
    // The converter APPENDS, so clear the stray empty paragraph first —
    // otherwise the result is [empty paragraph, ...seeded content].
    if (fragment.length > 0) fragment.delete(0, fragment.length);
    converters.fromJSON(schema, content, fragment);
    return 'seeded';
  } catch (err) {
    // Never crash the surface on malformed content, and report `skipped` so a
    // caller with a stash keeps it for a later retry.
    console.error('[note seed] failed to convert content', err);
    return 'skipped';
  }
}
